<script setup>

import { computed, ref, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { useCurrency } from '@/composables/currency'
import { useRateCard } from '@/composables/rate-card'
import { formatShortDate } from '@/lib/utils'

import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import GridDetailTabs from '@/components/shared/GridDetailTabs.vue'
import GridDetailInfo from '@/components/shared/GridDetailInfo.vue'
import WarehouseMovementsGrid from '@/components/planner/WarehouseMovementsGrid.vue'
import EquipmentConditionsGrid from '@/components/planner/EquipmentConditionsGrid.vue'

const props = defineProps({
  equipment: Object
})

const i18n = getI18n()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()

const { getCurrencyString } = useCurrency()
const { priceLabel, costLabel } = useRateCard()

const equipment = ref(props.equipment)
const equipmentQrcode = ref(null)
const equipmentStats = ref(null)
const activeTab = ref('info')

const currentCurrency = ref(webSiteStore.currencies.find(i => i.id === props.equipment.currency_ref) || webSiteStore.currencies.find(i => i.code === props.equipment.currency_ref_code) || webSiteStore.currencies.find(i => i.code === props.equipment.currency?.code) || webSiteStore.getDefaultCurrency())

const amount = (value) => getCurrencyString(value, currentCurrency.value.code)
const isSet = (value) => value !== null && value !== undefined && value !== ''

const identityItems = computed(() => [
  { label: i18n.t('common.name'), value: equipment.value.name },
  { label: i18n.t('common.code'), value: equipment.value.code },
  { label: i18n.t('common.serialNumber'), value: equipment.value.serial_number },
  { label: i18n.t('common.internalSerial'), value: equipment.value.internal_serial },
  { label: i18n.t('common.warehouse'), value: equipment.value.warehouse_name },
  { label: i18n.t('common.stock'), value: equipment.value.total_quantity || null },
  { label: i18n.t('common.rentalCompany'), value: equipment.value.is_rented ? equipment.value.rental_company_name : null },
  { label: i18n.t('common.price'), value: priceLabel(equipment.value, true) },
  { label: i18n.t('common.cost'), value: costLabel(equipment.value, true) },
  { label: i18n.t('common.buyDate'), value: equipment.value.buy_date && formatShortDate(equipment.value.buy_date) },
].filter(item => isSet(item.value)))

const statsItems = computed(() => {
  const stats = equipmentStats.value
  if (!stats) return []
  return [
    { label: i18n.t('common.purchaseCost'), value: isSet(stats.purchase_cost) ? amount(stats.purchase_cost) : null },
    { label: i18n.t('common.projectsCompleted'), value: `${stats.completed_project_count} / ${stats.project_count}` },
    { label: i18n.t('common.revenueCompleted'), value: `${amount(stats.completed_revenue)} / ${amount(stats.revenue)}` },
    { label: i18n.t('common.margin'), value: isSet(stats.margin) ? amount(stats.margin) : null, trend: stats.margin },
    { label: i18n.t('common.roi'), value: isSet(stats.roi) ? `${parseFloat(stats.roi).toFixed(2)}%` : null, trend: stats.roi },
    { label: i18n.t('common.criticalConditions'), value: stats.critical_status_changes > 0 ? stats.critical_status_changes : null, class: 'text-negative' },
  ].filter(item => isSet(item.value))
})

onMounted(async () => {
  equipment.value = { ...props.equipment, ...(await planningStore.fetchEquipmentByIdAsync(equipment.value.id)).data }
  equipmentQrcode.value = (await planningStore.fetchEquipmentQrcodeByIdAsync(equipment.value.id)).data
  if (webSiteStore.can(webSiteStore.permissionFlagEnum.FINANCIALS)) equipmentStats.value = (await planningStore.fetchEquipmentStatsByIdAsync(equipment.value.id)).data
})

</script>

<template>
  <grid-detail-tabs v-model="activeTab">
    <template #tabs>
      <q-tab name="info" :label="$t('common.info')" />
      <q-tab name="movements" :label="$t('menu.warehouses.movements')" />
      <q-tab name="conditions" :label="$t('common.condition', 2)" />
    </template>
    <template #panels>
      <q-tab-panel name="info" class="q-pa-none">
        <q-card-section horizontal>
          <q-img
            v-if="equipment?.avatar_file || equipment?.avatar_url"
            class="q-ma-md"
            :src="equipment.avatar_file || equipment.avatar_url"
            fit="contain"
            style="max-width: 12rem; max-height: 12rem;"
          />
          <q-icon
            v-else
            class="q-ma-md"
            size="12rem"
            color="grey-5"
            name="mdi-camera-off"
          />
          <q-separator
            vertical
            class="q-ma-md"
          />
          <q-img
            class="q-ma-md"
            :src="equipmentQrcode?.qr_code"
            fit="contain"
            style="max-width: 12rem; max-height: 12rem; width: 12rem; height: 12rem;"
          />
          <q-separator
            vertical
            class="q-ma-md"
          />

          <grid-detail-info class="col q-py-md q-pr-md" :items="[...identityItems, ...statsItems]">
            <div v-if="equipment.condition_value != null" class="grid-detail-info__wide">
              <div class="text-caption text-grey-7">{{ $t('common.condition') }}</div>
              <div class="row items-center no-wrap q-gutter-x-sm">
                <chip-template :values="planningStore.getLocalizedEquipmentConditions().filter(condition => condition.id === equipment.condition_value)" />
                <span v-if="equipment.condition_comment" class="text-body2 text-grey-8 ellipsis">{{ equipment.condition_comment }}</span>
              </div>
            </div>
          </grid-detail-info>
        </q-card-section>
      </q-tab-panel>

      <q-tab-panel name="movements" class="q-pa-none">
        <warehouse-movements-grid :service="equipment" />
      </q-tab-panel>

      <q-tab-panel name="conditions" class="q-pa-none">
        <equipment-conditions-grid :equipment="equipment" />
      </q-tab-panel>
    </template>
  </grid-detail-tabs>
</template>
